import {
  Skeleton,
  TextField,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import { useState, useEffect, useRef } from "react";
import {
  BannerSectionWrapper,
  BannerSectionContent,
  BannerSectionText,
  BannerSectionButton,
  BannerButtonText,
} from "../styles/components";
import { Box } from "@mui/material";

import { usePlanContext } from "../../consteNew/context/planContext";
import ZipCodeErrorModal from "./ZipCodeErrorModal";

interface BannerSectionTxProps {
  planSectionRef: React.RefObject<HTMLDivElement>;
}

export default function BannerSectionTx({ planSectionRef }: BannerSectionTxProps) {
  const {
    zipCode,
    setZipCode,
    plansLoading,
    zipErrorModal,
    setZipErrorModal,
    utility,
    plans,
    saveZipToStorage,
  } = usePlanContext();
  const [loading, setLoading] = useState(false);
  const [localZipCode, setLocalZipCode] = useState(zipCode);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const shouldScrollRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    setLocalZipCode(zipCode);
  }, [zipCode]);


  // Check for zip code error from context
  useEffect(() => {
    if (zipErrorModal) {
      setZipErrorModal(false);
    }
  }, [zipErrorModal, setZipErrorModal]);

  // Auto-scroll when plans finish loading (if user clicked View Plans)
  useEffect(() => {
    
    if (shouldScrollRef.current && !plansLoading && plans.length > 0) {
      handleScroll();
      shouldScrollRef.current = false;
    }
  }, [plansLoading, plans]);

  const handleZipCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Only allow numbers and max 5 digits
    if (/^\d{0,5}$/.test(value)) {
      setLocalZipCode(value);
    }
  };

  const handleViewPlans = async () => {
    if (loading) return; // Prevent multiple clicks

    setLoading(true);
    saveZipToStorage(localZipCode);
    
    if (!localZipCode || localZipCode.length !== 5) {
      setModalMessage("Please check your ZIP format");
      setModalOpen(true);
      setLoading(false); // Reset loading state
      return;
    }

    
    shouldScrollRef.current = true;
    
    // If plans already exist (user re-searching), scroll immediately
    if (!plansLoading && plans.length > 0) {
      setTimeout(() => handleScroll(), 100);
    } 
    
    setZipCode(localZipCode);
    setLoading(false);
  };

  function handleScroll() {
  
    const element = planSectionRef.current;
    
    if (element) {
      const navbarHeight = 80; // Account for sticky navbar
      const elementPosition =
        element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - navbarHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    } 
  }

  return (
    <BannerSectionWrapper>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            maxWidth: "1200px",
            alignItems: "flex-end",
            gap: "40px",
            flex: "1 0 0",
            width: "100%",
          }}
        >
          <Box sx={{ flex: "1 0 0" }}>
            <Skeleton variant="text" width="100%" height={50} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="90%" height={50} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="70%" height={50} />
          </Box>
          <Skeleton
            variant="rounded"
            width={280}
            height={56}
            sx={{ borderRadius: "6px" }}
          />
        </Box>
      ) : (
        <BannerSectionContent>
          <BannerSectionText>
            Join your fellow Texans, make an easy switch.
          </BannerSectionText>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <Box>
              <TextField
                id="outlined-basic"
                label="Zip Code"
                variant="outlined"
                value={localZipCode}
                onChange={handleZipCodeChange}
                placeholder="Enter ZIP"
                // error={localZipCode.length > 0 && localZipCode.length !== 5}
                helperText={
                  localZipCode.length > 0 &&
                  localZipCode.length !== 5 &&
                  !zipErrorModal
                    ? "check ZIP format"
                    : ""
                }
                InputProps={{
                  endAdornment:
                    localZipCode.length > 0 &&
                    localZipCode.length !== 5 &&
                    !zipErrorModal ? (
                      <InputAdornment position="end">
                        <img
                          src="/images/landingpagev2/icons/error.svg"
                          alt="zip icon"
                          width={20}
                          height={20}
                        />
                      </InputAdornment>
                    ) : null,
                }}
                sx={{
                  width: 180,
                  height: 54,
                }}
              />
            </Box>
            <Box>
              <BannerSectionButton
                variant="contained"
                onClick={() => handleViewPlans()}
                disabled={plansLoading}
                sx={{
                  display: "flex",
                  width: "140px",
                  padding: "16px 20px",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {plansLoading ? (
                  <CircularProgress size={20} sx={{ color: "#FFF" }} />
                ) : (
                  <BannerButtonText
                    sx={{
                      color: "#FFF",
                      fontFamily: "Inter",
                      fontSize: "16px",
                      fontStyle: "normal",
                      fontWeight: 600,
                      lineHeight: "20px",
                      letterSpacing: "-0.15px",
                      cursor: "pointer",
                    }}
                  >
                    View Plans
                  </BannerButtonText>
                )}
              </BannerSectionButton>
            </Box>
          </Box>
        </BannerSectionContent>
      )}
      <ZipCodeErrorModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        message={modalMessage}
      />
    </BannerSectionWrapper>
  );
}
